import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import {
  Battery, Zap, Shield, Sun, Bike, Car, Factory, Home, Phone, Mail, MapPin,
  ChevronDown, ChevronRight, Menu, X, Check, Award, Settings, Cpu, Clock,
  ThermometerSun, Leaf, Download, MessageCircle, Users, Truck, Package,
  ArrowRight, Plus, Minus, Star, Quote, Calculator, CircuitBoard, Gauge,
  Box, Layers, ExternalLink, Play
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster, toast } from "sonner";
import BatteryConfigurator from "./BatteryConfigurator";
import WarrantyCheck from "./WarrantyCheck";
import AdminPanel from "./AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_energy-havolt/artifacts/x68m0xml_Final%20LOGO.png";

// Brochure PDF URL
const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_energy-havolt/artifacts/t62kfr1y_HAVOLT%20Catalogue.pdf";

// Product Images from design guidelines
const PRODUCT_IMAGES = {
  cells: "https://images.unsplash.com/photo-1742899273038-67ff67477663?w=800&q=80",
  inverters: "https://images.unsplash.com/photo-1589275776107-e193042128bd?w=800&q=80",
  bms: "https://images.unsplash.com/photo-1555589228-5dc844368071?w=800&q=80",
  grading: "https://images.unsplash.com/photo-1744640326166-433469d102f2?w=800&q=80",
};

// Check if device prefers reduced motion or is mobile
const useReducedMotion = () => {
  const [prefersReduced, setPrefersReduced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mediaQuery.matches);
    
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    const handleChange = () => setPrefersReduced(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return prefersReduced || isMobile;
};

// Floating Particles Component for Hero - Disabled on mobile for performance
const FloatingParticles = () => {
  const reduceMotion = useReducedMotion();
  
  // Don't render particles on mobile or if user prefers reduced motion
  if (reduceMotion) return null;
  
  const particles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 15 + 15,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-havolt-green/20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isVisible, value]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "/products", label: "Products", isPage: true },
    { href: "#about", label: "About" },
    { href: "#compare", label: "Why LiFePO4" },
    { href: "/configurator", label: "Configurator", isPage: true },
    { href: "/warranty", label: "Warranty", isPage: true },
    { href: "#contact", label: "Contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled ? "glass-header shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <img
                src={LOGO_URL}
                alt="Havolt"
                className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.isPage ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-havolt-green transition-colors relative group"
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-havolt-green scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-havolt-green transition-colors relative group"
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-havolt-green scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                  </a>
                )
              ))}
              <a
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-4 px-5 py-2.5 bg-havolt-green text-black font-semibold text-sm rounded-full hover:bg-havolt-green-hover transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-havolt-green/20"
                data-testid="nav-brochure"
              >
                Download Catalogue
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-zinc-300 hover:text-havolt-green"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden pt-20 bg-havolt-dark"
          >
            <div className="container mx-auto px-6 py-8 flex flex-col gap-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.isPage ? (
                    <Link
                      to={link.href}
                      className="block py-3 text-xl font-medium text-zinc-200 hover:text-havolt-green transition-colors border-b border-zinc-800"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="block py-3 text-xl font-medium text-zinc-200 hover:text-havolt-green transition-colors border-b border-zinc-800"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  )}
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 py-4 text-center bg-havolt-green text-black font-bold rounded-lg"
              >
                Download Catalogue
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const reduceMotion = useReducedMotion();
  
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-havolt-dark via-havolt-paper to-havolt-dark" />
      
      {/* Animated Grid - hidden on mobile */}
      <div className="absolute inset-0 grid-pattern hidden md:block" />
      
      {/* Floating Particles - automatically disabled on mobile via the component */}
      <FloatingParticles />
      
      {/* Gradient Orb - simplified on mobile */}
      <div className="absolute top-1/4 right-1/4 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-havolt-green/5 rounded-full blur-[60px] md:blur-[120px]" />
      
      <div className="container mx-auto px-6 lg:px-12 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.8, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-havolt-subtle border border-havolt-border rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-havolt-green rounded-full animate-pulse" />
              <span className="text-sm font-medium text-havolt-muted">Premium LiFePO4 Solutions</span>
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-6">
              <span className="text-zinc-100">Power Your Future</span>
              <br />
              <span className="text-gradient-green">With Havolt</span>
            </h1>
            
            <p className="text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg">
              Industry-leading LiFePO4 batteries designed for reliability, longevity, and performance. From cells to complete battery systems.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <a
                href="#products"
                className="inline-flex items-center gap-2 px-8 py-4 bg-havolt-green text-black font-semibold rounded-full hover:bg-havolt-green-hover transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-havolt-green/20"
                data-testid="hero-cta-products"
              >
                View Products
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border border-zinc-600 text-zinc-200 font-semibold rounded-full hover:border-havolt-green hover:text-havolt-green transition-all"
                data-testid="hero-cta-contact"
              >
                Get Quote
              </a>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8">
              {[
                { value: 6000, suffix: "+", label: "Cycle Life" },
                { value: 10, suffix: " Yrs", label: "Lifespan" },
                { value: 99, suffix: "%", label: "Efficiency" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="text-center lg:text-left"
                >
                  <div className="text-2xl lg:text-3xl font-bold text-havolt-green font-mono">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Right - Battery Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Battery Stack Visualization */}
              <div className="relative mx-auto w-80">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.15 }}
                    className="relative mb-3"
                  >
                    <div 
                      className="h-16 bg-gradient-to-r from-havolt-paper to-havolt-subtle border border-havolt-border rounded-lg flex items-center px-6 overflow-hidden"
                      style={{ 
                        boxShadow: `0 ${4 + i * 2}px ${20 + i * 5}px rgba(0,0,0,0.3)`,
                      }}
                    >
                      {/* Energy bar animation */}
                      <motion.div
                        className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-havolt-green/20 to-havolt-green/5"
                        initial={{ width: "0%" }}
                        animate={{ width: `${70 + i * 6}%` }}
                        transition={{ delay: 1 + i * 0.2, duration: 1.5, ease: "easeOut" }}
                      />
                      <div className="relative flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <Battery className="h-5 w-5 text-havolt-green" />
                          <span className="font-mono text-sm text-zinc-300">Cell {i + 1}</span>
                        </div>
                        <span className="font-mono text-xs text-havolt-green">3.2V</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* Glow effect */}
                <div className="absolute -inset-10 bg-havolt-green/5 rounded-full blur-3xl -z-10" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator - hidden on mobile */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block">
        <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-1.5 bg-havolt-green rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Products Data (fallback categories)
const CATEGORY_INFO = {
  cells: { title: "LiFePO4 Cells", icon: Battery, color: "text-emerald-400" },
  packs: { title: "Battery Packs", icon: Package, color: "text-blue-400" },
  inverters: { title: "Inverters", icon: Zap, color: "text-yellow-400" },
  bms: { title: "BMS Systems", icon: CircuitBoard, color: "text-purple-400" },
  grading: { title: "Cell Grading", icon: Gauge, color: "text-orange-400" },
};

// Homepage Products Preview (shows only 4 featured products)
const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Get featured products first, then fill with others, limit to 4
      const allProducts = response.data;
      const featured = allProducts.filter(p => p.is_featured);
      const others = allProducts.filter(p => !p.is_featured);
      const displayProducts = [...featured, ...others].slice(0, 4);
      setProducts(displayProducts);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section id="products" className="py-24 bg-havolt-dark relative" data-testid="products-section">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-havolt-green mb-4 block">
            Our Products
          </span>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-4">
            Featured Products
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Premium LiFePO4 batteries, inverters, and accessories for all your energy storage needs.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-2 border-havolt-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-500">Loading products...</p>
          </div>
        ) : (
          <>
            {/* Products Grid - Only 4 featured */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => {
                const CategoryIcon = CATEGORY_INFO[product.category]?.icon || Package;
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ delay: Math.min(index * 0.05, 0.15), duration: 0.3 }}
                    className="group relative bg-havolt-paper border border-havolt-border rounded-xl overflow-hidden card-hover"
                    data-testid={`product-${product.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image_url || PRODUCT_IMAGES[product.category] || PRODUCT_IMAGES.cells}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover md:transition-transform md:duration-500 md:group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-havolt-paper via-transparent to-transparent" />
                      
                      {/* Category badge */}
                      <div className="absolute top-3 left-3 px-3 py-1.5 bg-havolt-dark/80 backdrop-blur-sm rounded-full border border-havolt-border flex items-center gap-2">
                        <CategoryIcon className="h-3.5 w-3.5 text-havolt-green" />
                        <span className="text-xs text-zinc-300">{CATEGORY_INFO[product.category]?.title || product.category}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-zinc-100 mb-2 group-hover:text-havolt-green transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                      
                      {/* Price */}
                      <div className="text-xl font-bold text-havolt-green mb-3">
                        {formatPrice(product.price)}
                      </div>

                      {/* Specs */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {(product.specs || []).slice(0, 2).map((spec, i) => (
                          <div
                            key={i}
                            className="px-2 py-1 bg-havolt-subtle rounded text-xs"
                          >
                            <span className="text-zinc-500">{spec.label}:</span>{" "}
                            <span className="text-zinc-300">{spec.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <a
                        href={`https://wa.me/919079059738?text=Hi%20Havolt,%20I'm%20interested%20in%20${encodeURIComponent(product.name)}%20(${formatPrice(product.price)})`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2.5 border border-havolt-border rounded-lg text-sm font-medium text-zinc-300 hover:border-havolt-green hover:text-havolt-green hover:bg-havolt-green/5 transition-all flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Enquire Now
                      </a>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}

        {/* View All Products & Download Catalogue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-8 py-4 bg-havolt-green text-black font-semibold rounded-full hover:bg-havolt-green-hover transition-all hover:-translate-y-1"
            data-testid="view-all-products-btn"
          >
            View All Products
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href={BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-havolt-subtle border border-havolt-border rounded-full hover:border-havolt-green hover:bg-havolt-paper transition-all group"
            data-testid="download-catalogue-btn"
          >
            <Download className="h-5 w-5 text-havolt-green" />
            <span className="text-zinc-200 font-medium">Download Catalogue</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    { icon: Factory, title: "Manufacturing", desc: "In-house assembly & quality control" },
    { icon: Award, title: "Quality", desc: "Grade-A cells from top brands" },
    { icon: Truck, title: "Delivery", desc: "Pan-India shipping network" },
    { icon: Users, title: "Support", desc: "Dedicated technical assistance" },
  ];

  return (
    <section id="about" className="py-24 bg-havolt-paper relative" data-testid="about-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-havolt-green mb-4 block">
              About Havolt
            </span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-6">
              Powering India&apos;s Clean Energy Transition
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Havolt, a brand of <strong className="text-zinc-200">Hari Om Enterprises</strong>, is committed to delivering premium LiFePO4 battery solutions for solar storage, electric vehicles, and industrial applications.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-8">
              We partner with leading cell manufacturers including CATL, BYD, and EVE Energy to ensure every battery pack meets the highest quality standards.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 bg-havolt-subtle border border-havolt-border rounded-lg"
                >
                  <feature.icon className="h-6 w-6 text-havolt-green mb-3" />
                  <h4 className="font-semibold text-zinc-200 mb-1">{feature.title}</h4>
                  <p className="text-sm text-zinc-500">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Partner Brands */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-havolt-subtle border border-havolt-border rounded-2xl p-8">
              <h3 className="text-lg font-heading font-bold text-zinc-200 mb-6 text-center">
                Our Partner Brands
              </h3>
              
              {/* Cell Brands */}
              <div className="mb-8">
                <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4 text-center">
                  LiFePO4 Cell Brands
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {["CATL", "BYD", "EVE Energy", "CALB", "Highstar", "Hithium"].map((brand) => (
                    <span
                      key={brand}
                      className="px-4 py-2 bg-havolt-paper border border-havolt-border rounded-full text-sm text-zinc-300"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>

              {/* BMS Brands */}
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4 text-center">
                  Smart BMS Brands
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {["DALY", "JBD", "JK", "Ant BMS"].map((brand) => (
                    <span
                      key={brand}
                      className="px-4 py-2 bg-havolt-paper border border-havolt-border rounded-full text-sm text-zinc-300"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-havolt-green/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-havolt-green/5 rounded-full blur-3xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Comparison Section
const ComparisonSection = () => {
  const comparisons = [
    { feature: "Cycle Life", lifepo4: "2000-6000 cycles", leadAcid: "300-500 cycles", winner: "lifepo4" },
    { feature: "Lifespan", lifepo4: "10-15 years", leadAcid: "3-5 years", winner: "lifepo4" },
    { feature: "Weight", lifepo4: "70% lighter", leadAcid: "Heavy", winner: "lifepo4" },
    { feature: "Efficiency", lifepo4: "95-99%", leadAcid: "80-85%", winner: "lifepo4" },
    { feature: "Maintenance", lifepo4: "Zero", leadAcid: "Regular", winner: "lifepo4" },
    { feature: "Safety", lifepo4: "Non-explosive", leadAcid: "Gas emission", winner: "lifepo4" },
    { feature: "Discharge Depth", lifepo4: "100% DoD", leadAcid: "50% DoD", winner: "lifepo4" },
    { feature: "Upfront Cost", lifepo4: "Higher", leadAcid: "Lower", winner: "leadAcid" },
  ];

  return (
    <section id="compare" className="py-24 bg-havolt-dark relative" data-testid="compare-section">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-havolt-green mb-4 block">
            Why LiFePO4?
          </span>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-4">
            LiFePO4 vs Lead Acid
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            See why lithium iron phosphate is the smart choice for modern energy storage.
          </p>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-havolt-paper border border-havolt-border rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-havolt-subtle border-b border-havolt-border">
              <div className="p-4 text-sm font-medium text-zinc-400">Feature</div>
              <div className="p-4 text-sm font-medium text-center text-havolt-green border-x border-havolt-border">
                LiFePO4
              </div>
              <div className="p-4 text-sm font-medium text-center text-zinc-400">Lead Acid</div>
            </div>

            {/* Rows */}
            {comparisons.map((row, index) => (
              <motion.div
                key={row.feature}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="grid grid-cols-3 border-b border-havolt-border last:border-b-0 hover:bg-havolt-subtle/50 transition-colors"
              >
                <div className="p-4 text-sm text-zinc-300 font-medium">{row.feature}</div>
                <div className={`p-4 text-sm text-center border-x border-havolt-border ${
                  row.winner === "lifepo4" ? "text-havolt-green bg-havolt-green/5" : "text-zinc-400"
                }`}>
                  {row.winner === "lifepo4" && <Check className="h-4 w-4 inline mr-2" />}
                  {row.lifepo4}
                </div>
                <div className={`p-4 text-sm text-center ${
                  row.winner === "leadAcid" ? "text-zinc-200" : "text-zinc-500"
                }`}>
                  {row.leadAcid}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link
            to="/configurator"
            className="inline-flex items-center gap-2 px-8 py-4 bg-havolt-green text-black font-semibold rounded-full hover:bg-havolt-green-hover transition-all hover:-translate-y-1"
            data-testid="compare-configurator-cta"
          >
            <Calculator className="h-5 w-5" />
            Configure Your Battery
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Applications Section
const ApplicationsSection = () => {
  const applications = [
    { icon: Sun, title: "Solar Storage", desc: "Home & commercial solar systems" },
    { icon: Bike, title: "E-Scooters", desc: "Two-wheeler electric vehicles" },
    { icon: Car, title: "E-Rickshaws", desc: "Three-wheeler EVs & carts" },
    { icon: Factory, title: "Industrial", desc: "UPS & backup power systems" },
    { icon: Home, title: "Home Backup", desc: "Residential power backup" },
    { icon: Truck, title: "Commercial EVs", desc: "Delivery vehicles & logistics" },
  ];

  return (
    <section id="applications" className="py-24 bg-havolt-paper relative" data-testid="applications-section">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-havolt-green mb-4 block">
            Applications
          </span>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-4">
            Powering Every Application
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {applications.map((app, index) => (
            <motion.div
              key={app.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-6 bg-havolt-subtle border border-havolt-border rounded-xl text-center hover:border-havolt-green hover:bg-havolt-green/5 transition-all card-hover"
              data-testid={`app-${app.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-havolt-paper border border-havolt-border rounded-xl group-hover:border-havolt-green group-hover:bg-havolt-green/10 transition-colors">
                <app.icon className="h-7 w-7 text-havolt-green" />
              </div>
              <h3 className="font-semibold text-zinc-200 mb-1">{app.title}</h3>
              <p className="text-xs text-zinc-500">{app.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Inquiry submitted successfully! We'll contact you soon.");
      setFormData({ name: "", email: "", phone: "", inquiry_type: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-havolt-dark relative" data-testid="contact-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-havolt-green mb-4 block">
              Contact Us
            </span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-6">
              Let&apos;s Power Your Project
            </h2>
            <p className="text-zinc-400 mb-8">
              Have questions or need a custom quote? Our team is ready to help you find the perfect battery solution.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-havolt-subtle border border-havolt-border rounded-lg">
                  <Phone className="h-5 w-5 text-havolt-green" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 mb-1">Phone</p>
                  <a href="tel:+919079059738" className="text-zinc-200 hover:text-havolt-green transition-colors">
                    +91 9079059738
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-havolt-subtle border border-havolt-border rounded-lg">
                  <Mail className="h-5 w-5 text-havolt-green" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 mb-1">Email</p>
                  <a href="mailto:havoltbattery@gmail.com" className="text-zinc-200 hover:text-havolt-green transition-colors">
                    havoltbattery@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-havolt-subtle border border-havolt-border rounded-lg">
                  <MapPin className="h-5 w-5 text-havolt-green" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 mb-1">Address</p>
                  <p className="text-zinc-200">
                    M-30, 31, Ganpati Plaza, Phool Bagh,<br />
                    Bhiwadi, Rajasthan, 301019
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/919079059738?text=Hi%20Havolt,%20I'm%20interested%20in%20your%20LiFePO4%20batteries"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-8 px-6 py-4 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#20bd5a] transition-all"
              data-testid="whatsapp-contact"
            >
              <MessageCircle className="h-5 w-5" />
              Chat on WhatsApp
            </a>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-havolt-paper border border-havolt-border rounded-2xl p-8">
              <div className="grid gap-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-zinc-400 mb-2 block">Name *</Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-havolt-subtle border-havolt-border focus:border-havolt-green"
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-zinc-400 mb-2 block">Phone *</Label>
                    <Input
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-havolt-subtle border-havolt-border focus:border-havolt-green"
                      placeholder="Your phone"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-zinc-400 mb-2 block">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-havolt-subtle border-havolt-border focus:border-havolt-green"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-400 mb-2 block">Subject *</Label>
                  <Select
                    value={formData.inquiry_type}
                    onValueChange={(value) => setFormData({ ...formData, inquiry_type: value })}
                  >
                    <SelectTrigger className="bg-havolt-subtle border-havolt-border" data-testid="contact-subject">
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent className="bg-havolt-paper border-havolt-border">
                      <SelectItem value="quote">Get a Quote</SelectItem>
                      <SelectItem value="distributor">Distributor Inquiry</SelectItem>
                      <SelectItem value="bulk">Bulk Order</SelectItem>
                      <SelectItem value="general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm text-zinc-400 mb-2 block">Message *</Label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-havolt-subtle border-havolt-border focus:border-havolt-green min-h-[120px]"
                    placeholder="Tell us about your requirements..."
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-6 bg-havolt-green text-black font-semibold rounded-lg hover:bg-havolt-green-hover transition-all"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? "Sending..." : "Send Inquiry"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-havolt-paper border-t border-havolt-border" data-testid="footer">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Havolt" className="h-10 w-auto" />
            <div className="text-sm text-zinc-500">
              A brand of <span className="text-zinc-300">Hari Om Enterprises</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-zinc-500">
            <Link to="/products" className="hover:text-havolt-green transition-colors">Products</Link>
            <a href="#about" className="hover:text-havolt-green transition-colors">About</a>
            <Link to="/configurator" className="hover:text-havolt-green transition-colors">Configurator</Link>
            <Link to="/warranty" className="hover:text-havolt-green transition-colors">Warranty</Link>
            <a href="#contact" className="hover:text-havolt-green transition-colors">Contact</a>
          </div>

          <div className="text-sm text-zinc-600">
            © {new Date().getFullYear()} Havolt. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/919079059738?text=Hi%20Havolt,%20I'm%20interested%20in%20your%20LiFePO4%20batteries"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 p-4 bg-[#25D366] text-white rounded-full shadow-lg whatsapp-pulse hover:scale-110 transition-transform"
      data-testid="whatsapp-float"
    >
      <MessageCircle className="h-6 w-6" />
    </a>
  );
};

// Full Products Page
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["all", "cells", "packs", "inverters", "bms", "grading"];
  
  const filteredProducts = activeCategory === "all" 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-havolt-dark">
      {/* Simple Header */}
      <header className="bg-havolt-dark/95 backdrop-blur-lg border-b border-havolt-border sticky top-0 z-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Havolt" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-sm text-zinc-400 hover:text-havolt-green transition-colors"
              >
                Back to Home
              </Link>
              <a
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-havolt-green text-black font-semibold text-sm rounded-full hover:bg-havolt-green-hover transition-all"
              >
                <Download className="h-4 w-4" />
                Catalogue
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 lg:px-12 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-zinc-100 mb-4">
            All Products
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Browse our complete range of LiFePO4 batteries, inverters, BMS systems, and accessories.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? "bg-havolt-green text-black"
                  : "bg-havolt-subtle border border-havolt-border text-zinc-400 hover:border-havolt-green hover:text-havolt-green"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat === "all" ? "All Products" : CATEGORY_INFO[cat]?.title || cat}
            </button>
          ))}
        </div>

        {/* Products Count */}
        <div className="mb-6 text-sm text-zinc-500">
          Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          {activeCategory !== "all" && ` in ${CATEGORY_INFO[activeCategory]?.title || activeCategory}`}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-2 border-havolt-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-500">Loading products...</p>
          </div>
        ) : (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => {
                const CategoryIcon = CATEGORY_INFO[product.category]?.icon || Package;
                return (
                  <div
                    key={product.id}
                    className="group relative bg-havolt-paper border border-havolt-border rounded-xl overflow-hidden card-hover"
                    data-testid={`product-${product.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image_url || PRODUCT_IMAGES[product.category] || PRODUCT_IMAGES.cells}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover md:transition-transform md:duration-500 md:group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-havolt-paper via-transparent to-transparent" />
                      
                      {/* Category badge */}
                      <div className="absolute top-3 left-3 px-3 py-1.5 bg-havolt-dark/80 backdrop-blur-sm rounded-full border border-havolt-border flex items-center gap-2">
                        <CategoryIcon className="h-3.5 w-3.5 text-havolt-green" />
                        <span className="text-xs text-zinc-300">{CATEGORY_INFO[product.category]?.title || product.category}</span>
                      </div>

                      {/* Featured badge */}
                      {product.is_featured && (
                        <div className="absolute top-3 right-3 px-2 py-1 bg-havolt-green text-black text-xs font-bold rounded">
                          Featured
                        </div>
                      )}

                      {/* Out of stock */}
                      {!product.in_stock && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white font-bold">Out of Stock</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-zinc-100 mb-2 group-hover:text-havolt-green transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                      
                      {/* Price */}
                      <div className="text-xl font-bold text-havolt-green mb-3">
                        {formatPrice(product.price)}
                      </div>

                      {/* Specs */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(product.specs || []).slice(0, 3).map((spec, i) => (
                          <div
                            key={i}
                            className="px-2 py-1 bg-havolt-subtle rounded text-xs"
                          >
                            <span className="text-zinc-500">{spec.label}:</span>{" "}
                            <span className="text-zinc-300">{spec.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Description */}
                      <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                        {product.description}
                      </p>

                      {/* CTA */}
                      <a
                        href={`https://wa.me/919079059738?text=Hi%20Havolt,%20I'm%20interested%20in%20${encodeURIComponent(product.name)}%20(${formatPrice(product.price)})`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2.5 border border-havolt-border rounded-lg text-sm font-medium text-zinc-300 hover:border-havolt-green hover:text-havolt-green hover:bg-havolt-green/5 transition-all flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Enquire Now
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* No products message */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-16">
                <Package className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No products found in this category.</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Simple Footer */}
      <footer className="py-8 border-t border-havolt-border">
        <div className="container mx-auto px-6 lg:px-12 text-center text-sm text-zinc-500">
          © {new Date().getFullYear()} Havolt by Hari Om Enterprises. All rights reserved.
        </div>
      </footer>

      <WhatsAppFloat />
    </div>
  );
};

// Main Landing Page
const LandingPage = () => {
  // Initialize Lenis smooth scroll - only on desktop
  useEffect(() => {
    // Skip Lenis on mobile/touch devices for better performance
    const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
    if (isMobile) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return (
    <main className="overflow-hidden">
      <Navigation />
      <HeroSection />
      <ProductsSection />
      <AboutSection />
      <ComparisonSection />
      <ApplicationsSection />
      <ContactSection />
      <Footer />
      <WhatsAppFloat />
    </main>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#1E221F",
            border: "1px solid #2C332E",
            color: "#E8EDE9",
          },
        }}
      />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/configurator" element={<BatteryConfigurator />} />
        <Route path="/warranty" element={<WarrantyCheck />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
